import React, { useEffect, useRef, useId, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import '../../styles/Modal.css';

/**
 * BaseModal - Reusable modal wrapper component with accessibility features
 * 
 * Props:
 * - isOpen: boolean - Whether modal is open
 * - onClose: function - Called when modal should close
 * - title: string - Modal title
 * - icon: ReactNode - Optional icon component for header
 * - size: 'small' | 'medium' | 'large' | 'xlarge' | 'fullwidth' | 'wizard' - Modal size
 * - children: ReactNode - Modal content
 * - footer: ReactNode - Optional custom footer
 * - showCloseButton: boolean - Whether to show X button (default: true)
 * - closeOnBackdrop: boolean - Whether clicking backdrop closes (default: true)
 * - closeOnEsc: boolean - Whether ESC key closes modal (default: true)
 * - lockBodyScroll: boolean - Prevent body scroll when open (default: true)
 * - autoFocus: boolean - Auto-focus first focusable element (default: true)
 * - returnFocus: boolean - Return focus to trigger on close (default: true)
 * - disabled: boolean - Disable close interactions
 * - hidden: boolean - Hide modal (for stacked modals)
 * - ariaDescribedBy: string - ID of element describing modal content
 */
function BaseModal({ 
  isOpen, 
  onClose, 
  title, 
  icon,
  size = 'medium',
  children, 
  footer,
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEsc = true,
  lockBodyScroll = true,
  autoFocus = true,
  returnFocus = true,
  disabled = false,
  hidden = false,
  ariaDescribedBy
}) {
  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const titleId = useId();
  
  // Store the element that triggered the modal
  useEffect(() => {
    if (isOpen && returnFocus) {
      triggerRef.current = document.activeElement;
    }
  }, [isOpen, returnFocus]);

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEsc || disabled || hidden) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, disabled, hidden, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen || !lockBodyScroll || hidden) return;
    
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen, lockBodyScroll, hidden]);

  // Auto-focus first focusable element
  useEffect(() => {
    if (!isOpen || !autoFocus || hidden) return;
    
    // Small delay to ensure modal is rendered
    const timeoutId = setTimeout(() => {
      if (modalRef.current) {
        // Find first focusable element
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        // Focus the close button or first focusable element
        const closeButton = modalRef.current.querySelector('.modal-close');
        if (closeButton && showCloseButton) {
          closeButton.focus();
        } else if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, autoFocus, hidden, showCloseButton]);

  // Return focus to trigger element on close
  useEffect(() => {
    if (!isOpen && returnFocus && triggerRef.current) {
      // Small delay to ensure modal is fully closed
      const timeoutId = setTimeout(() => {
        if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
          triggerRef.current.focus();
        }
        triggerRef.current = null;
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, returnFocus]);

  // Focus trap - keep focus within modal
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    
    const focusableElements = modalRef.current.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey) {
      // Shift + Tab: if on first element, go to last
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: if on last element, go to first
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdrop && !disabled) {
      onClose();
    }
  };

  const sizeClass = {
    small: 'modal-small',
    medium: 'modal-medium',
    large: 'modal-large',
    xlarge: 'modal-xlarge',
    fullwidth: 'modal-fullwidth',
    wizard: 'modal-wizard',
    lg: 'modal-xlarge',  // Alias for backwards compatibility
  }[size] || '';

  const modal = (
    <div 
      className={`modal-backdrop ${hidden ? 'modal-hidden' : ''}`} 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div 
        ref={modalRef}
        className={`modal-container ${sizeClass}`} 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={ariaDescribedBy}
      >
        <div className="modal-header">
          <div className="modal-title-wrapper">
            {icon}
            <h3 id={titleId}>{title}</h3>
          </div>
          {showCloseButton && (
            <button 
              className="modal-close" 
              onClick={onClose} 
              disabled={disabled}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          )}
        </div>
        
        {children}
        
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render in portal to avoid z-index issues
  return ReactDOM.createPortal(modal, document.body);
}

export default BaseModal;
